// PitayaClient - tối giản WebSocket client tương thích Pitaya/JSON serializer.
// PRD_SystemDesign 5.1: hỗ trợ request/response + push notification.
//
// Pitaya wire protocol (simplified):
//   Packet header (4B): type(1) | length(3 BE)
//   Packet types: HANDSHAKE=1, HANDSHAKE_ACK=2, HEARTBEAT=3, DATA=4, KICK=5
//   Inside DATA: Message (msgType:1, id:varint?, route, body)
//
// Để giữ cho client gọn và dễ swap sang protobuf sau (PRD note),
// lớp này expose API đơn giản: request(route, payload), notify, on(event).

type Listener = (data: unknown) => void;

export interface PitayaOptions {
  url: string;
  token?: string;
  onOpen?: () => void;
  onClose?: (code: number) => void;
  onError?: (e: Event) => void;
  onKick?: () => void;
  heartbeatMs?: number;
}

const PKT_HANDSHAKE = 1;
const PKT_HANDSHAKE_ACK = 2;
const PKT_HEARTBEAT = 3;
const PKT_DATA = 4;
const PKT_KICK = 5;

const MSG_REQUEST = 0;
const MSG_NOTIFY = 1;
const MSG_RESPONSE = 2;
const MSG_PUSH = 3;

function encodePacket(type: number, body: Uint8Array): Uint8Array {
  const len = body.length;
  const buf = new Uint8Array(4 + len);
  buf[0] = type;
  buf[1] = (len >> 16) & 0xff;
  buf[2] = (len >> 8) & 0xff;
  buf[3] = len & 0xff;
  buf.set(body, 4);
  return buf;
}

function decodePackets(buf: Uint8Array): Array<{ type: number; body: Uint8Array }> {
  const out: Array<{ type: number; body: Uint8Array }> = [];
  let off = 0;
  while (off + 4 <= buf.length) {
    const type = buf[off];
    const len = (buf[off + 1] << 16) | (buf[off + 2] << 8) | buf[off + 3];
    if (off + 4 + len > buf.length) break;
    out.push({ type, body: buf.slice(off + 4, off + 4 + len) });
    off += 4 + len;
  }
  return out;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

function writeVarint(n: number): number[] {
  const out: number[] = [];
  while (n >= 0x80) {
    out.push((n & 0x7f) | 0x80);
    n >>>= 7;
  }
  out.push(n & 0x7f);
  return out;
}

function readVarint(buf: Uint8Array, off: number): { val: number; off: number } {
  let val = 0, shift = 0, cur = off;
  while (cur < buf.length) {
    const b = buf[cur++];
    val |= (b & 0x7f) << shift;
    if ((b & 0x80) === 0) return { val, off: cur };
    shift += 7;
  }
  return { val, off: cur };
}

function encodeMessage(msgType: number, id: number, route: string, body: Uint8Array): Uint8Array {
  // flag byte: bit 0..2 type, bit 3 route compression (0)
  const flag = (msgType & 0x7) << 1;
  const idBytes = msgType === MSG_REQUEST || msgType === MSG_RESPONSE ? writeVarint(id) : [];
  const routeBytes = enc.encode(route);
  const out = new Uint8Array(1 + idBytes.length + 1 + routeBytes.length + body.length);
  let off = 0;
  out[off++] = flag;
  for (const b of idBytes) out[off++] = b;
  out[off++] = routeBytes.length;
  out.set(routeBytes, off); off += routeBytes.length;
  out.set(body, off);
  return out;
}

const GZIP_MASK = 0x10;

function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate');
  const writer = ds.writable.getWriter();
  writer.write(data as unknown as ArrayBufferView<ArrayBuffer>);
  writer.close();
  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];
  return (async () => {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    const total = chunks.reduce((s, c) => s + c.length, 0);
    const out = new Uint8Array(total);
    let off = 0;
    for (const c of chunks) { out.set(c, off); off += c.length; }
    return out;
  })();
}

function decodeMessage(buf: Uint8Array): { msgType: number; id: number; route: string; body: Uint8Array; compressed: boolean } {
  let off = 0;
  const flag = buf[off++];
  const msgType = (flag >> 1) & 0x7;
  const compressed = (flag & GZIP_MASK) !== 0;
  let id = 0;
  if (msgType === MSG_REQUEST || msgType === MSG_RESPONSE) {
    const v = readVarint(buf, off); id = v.val; off = v.off;
  }
  let route = '';
  if (msgType !== MSG_RESPONSE) {
    const rlen = buf[off++];
    route = dec.decode(buf.slice(off, off + rlen));
    off += rlen;
  }
  return { msgType, id, route, body: buf.slice(off), compressed };
}

export class PitayaClient {
  private ws?: WebSocket;
  private opts: PitayaOptions;
  private reqId = 0;
  private pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
  private listeners = new Map<string, Set<Listener>>();
  private hbTimer?: number;
  private routeIdMap = new Map<number, string>();
  private connected = false;

  constructor(opts: PitayaOptions) {
    this.opts = { heartbeatMs: 5000, ...opts };
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = this.opts.token
        ? `${this.opts.url}?token=${encodeURIComponent(this.opts.token)}`
        : this.opts.url;
      this.ws = new WebSocket(url);
      this.ws.binaryType = 'arraybuffer';
      this.ws.onopen = () => {
        const handshake = enc.encode(JSON.stringify({
          sys: { platform: 'web', libVersion: '0.1', clientBuildNumber: '1', clientVersion: '0.1' },
          user: { token: this.opts.token ?? '' },
        }));
        this.ws!.send(encodePacket(PKT_HANDSHAKE, handshake));
      };
      this.ws.onmessage = (ev) => {
        const buf = new Uint8Array(ev.data as ArrayBuffer);
        const pkts = decodePackets(buf);
        for (const p of pkts) this.handlePacket(p, resolve);
      };
      this.ws.onerror = (e) => { this.opts.onError?.(e); if (!this.connected) reject(new Error('ws error')); };
      this.ws.onclose = (ev) => {
        this.connected = false;
        if (this.hbTimer) clearInterval(this.hbTimer);
        this.opts.onClose?.(ev.code);
      };
    });
  }

  private handlePacket(p: { type: number; body: Uint8Array }, onReady?: () => void) {
    switch (p.type) {
      case PKT_HANDSHAKE: {
        try {
          const data = JSON.parse(dec.decode(p.body));
          const codes = data?.sys?.dict as Record<string, number> | undefined;
          if (codes) for (const [route, id] of Object.entries(codes)) this.routeIdMap.set(id, route);
        } catch { /* ignore */ }
        this.ws!.send(encodePacket(PKT_HANDSHAKE_ACK, new Uint8Array(0)));
        this.connected = true;
        this.startHeartbeat();
        this.opts.onOpen?.();
        onReady?.();
        break;
      }
      case PKT_HEARTBEAT:
        // server hb -> reply
        this.ws!.send(encodePacket(PKT_HEARTBEAT, new Uint8Array(0)));
        break;
      case PKT_DATA: {
        const m = decodeMessage(p.body);
        const processBody = async (body: Uint8Array) => {
          const raw = m.compressed ? await inflateRaw(body) : body;
          let payload: unknown;
          try { payload = JSON.parse(dec.decode(raw)); } catch { payload = raw; }
          return payload;
        };
        processBody(m.body).then((payload) => {
          if (m.msgType === MSG_RESPONSE) {
            const pend = this.pending.get(m.id);
            if (pend) { this.pending.delete(m.id); pend.resolve(payload); }
          } else if (m.msgType === MSG_PUSH) {
            this.emit(m.route, payload);
          }
        }).catch(() => { /* swallow decompression errors */ });
        break;
      }
      case PKT_KICK:
        this.opts.onKick?.();
        this.ws?.close();
        break;
    }
  }

  private startHeartbeat() {
    this.hbTimer = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(encodePacket(PKT_HEARTBEAT, new Uint8Array(0)));
      }
    }, this.opts.heartbeatMs!);
  }

  request<T = unknown>(route: string, payload: unknown = {}): Promise<T> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return Promise.reject(new Error('not connected'));
    const id = ++this.reqId;
    const body = enc.encode(JSON.stringify(payload));
    const msg = encodeMessage(MSG_REQUEST, id, route, body);
    this.ws.send(encodePacket(PKT_DATA, msg));
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: (v) => resolve(v as T), reject });
      setTimeout(() => {
        if (this.pending.has(id)) { this.pending.delete(id); reject(new Error(`timeout: ${route}`)); }
      }, 10000);
    });
  }

  notify(route: string, payload: unknown = {}): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const body = enc.encode(JSON.stringify(payload));
    const msg = encodeMessage(MSG_NOTIFY, 0, route, body);
    this.ws.send(encodePacket(PKT_DATA, msg));
  }

  on(route: string, fn: Listener): () => void {
    if (!this.listeners.has(route)) this.listeners.set(route, new Set());
    this.listeners.get(route)!.add(fn);
    return () => this.listeners.get(route)?.delete(fn);
  }

  private emit(route: string, data: unknown) {
    this.listeners.get(route)?.forEach((fn) => { try { fn(data); } catch { /* swallow */ } });
  }

  close() { this.ws?.close(); }
  isConnected() { return this.connected; }
}

let singleton: PitayaClient | null = null;
export function getPitayaClient(): PitayaClient | null { return singleton; }
export function setPitayaClient(c: PitayaClient | null) { singleton = c; }
